// src/services/sseTokenService.js
import { BASE, getToken, getAuthHeaders } from '../config/api';

class SSETokenService {
  constructor() {
    this.token = null;
    this.expiresAt = null;
    this.renewalTimer = null;
    this.isRenewing = false;
  }

  /**
   * Récupère un token SSE temporaire depuis le backend
   */
  async getSSEToken() {
    console.log('🔄 [SSEToken] Demande d\'un nouveau token SSE...');
    
    // Vérifier le token principal avant de faire la requête
    const mainToken = getToken();
    if (!mainToken) {
      console.error('❌ [SSEToken] Pas de token principal disponible');
      throw new Error('Token principal manquant');
    }
    
    console.log('🔍 [SSEToken] Token principal length:', mainToken.length);
    
    try {
      // Utiliser le vrai endpoint SSE du backend Stericab
      console.log('🔄 [SSEToken] Demande token SSE au backend Stericab...');
      
      const response = await fetch(`${BASE}/api/sse/token`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ [SSEToken] Erreur récupération token:', response.status, errorData);
        
        // FALLBACK: Si le token temporaire échoue, utiliser le token principal
        if (response.status === 401 || response.status === 404) {
          console.warn('⚠️ [SSEToken] Fallback vers token principal');
          const mainToken = getToken();
          if (!mainToken) throw new Error('Token principal manquant');
          
          this.token = mainToken;
          this.expiresAt = Date.now() + (4 * 60 * 60 * 1000); // 4h
          this.scheduleRenewal();
          return this.token;
        }
        
        throw new Error(`Erreur ${response.status}: ${errorData.message || 'Token SSE non disponible'}`);
      }

      const data = await response.json();
      this.token = data.sseToken;
      
      // Calculer l'expiration (5h - 30min de marge)
      this.expiresAt = Date.now() + (4.5 * 60 * 60 * 1000); // 4h30
      
      console.log('✅ [SSEToken] Token SSE obtenu:', {
        tokenLength: this.token.length,
        expiresIn: '4h30',
        expiresAt: new Date(this.expiresAt).toISOString()
      });

      // Programmer le renouvellement automatique
      this.scheduleRenewal();
      
      return this.token;
    } catch (error) {
      console.error('❌ [SSEToken] Erreur fatale:', error);
      throw error;
    }
  }

  /**
   * Programme le renouvellement automatique du token
   */
  scheduleRenewal() {
    // Annuler le timer précédent
    if (this.renewalTimer) {
      clearTimeout(this.renewalTimer);
    }

    // Calculer le délai avant expiration (30min avant)
    const renewalDelay = this.expiresAt - Date.now() - (30 * 60 * 1000);
    
    if (renewalDelay > 0) {
      console.log('⏰ [SSEToken] Renouvellement programmé dans', Math.round(renewalDelay / 60000), 'minutes');
      
      this.renewalTimer = setTimeout(() => {
        this.renewToken();
      }, renewalDelay);
    }
  }

  /**
   * Renouvelle le token SSE
   */
  async renewToken() {
    if (this.isRenewing) {
      console.log('⏳ [SSEToken] Renouvellement déjà en cours...');
      return;
    }

    this.isRenewing = true;
    console.log('🔄 [SSEToken] Renouvellement automatique du token SSE...');

    try {
      await this.getSSEToken();
      console.log('✅ [SSEToken] Token SSE renouvelé avec succès');
      
      // Émettre un événement pour redémarrer les connexions SSE
      window.dispatchEvent(new CustomEvent('sse-token-renewed', { 
        detail: { newToken: this.token } 
      }));
    } catch (error) {
      console.error('❌ [SSEToken] Échec du renouvellement:', error);
      
      // Réessayer dans 5 minutes
      setTimeout(() => {
        this.renewToken();
      }, 5 * 60 * 1000);
    } finally {
      this.isRenewing = false;
    }
  }

  /**
   * Vérifie si le token est valide
   */
  isValid() {
    if (!this.token || !this.expiresAt) {
      return false;
    }
    
    const now = Date.now();
    const timeUntilExpiry = this.expiresAt - now;
    
    // Considérer comme invalide si expiration dans moins de 5 minutes
    return timeUntilExpiry > (5 * 60 * 1000);
  }

  /**
   * Force le renouvellement du token
   */
  async forceRenew() {
    console.log('🔄 [SSEToken] Renouvellement forcé du token SSE...');
    this.token = null;
    this.expiresAt = null;
    await this.getSSEToken();
  }

  /**
   * Nettoie les ressources
   */
  cleanup() {
    if (this.renewalTimer) {
      clearTimeout(this.renewalTimer);
      this.renewalTimer = null;
    }
    this.token = null;
    this.expiresAt = null;
    this.isRenewing = false;
    console.log('🧹 [SSEToken] Service nettoyé');
  }

  /**
   * Obtient le token actuel (le récupère si nécessaire)
   */
  async getCurrentToken() {
    if (!this.isValid()) {
      console.log('🔄 [SSEToken] Token invalide, récupération...');
      await this.getSSEToken();
    }
    return this.token;
  }
}

// Instance singleton
const sseTokenService = new SSETokenService();

export default sseTokenService;
